import React, { useState, useEffect, useRef } from "react";
import { Step1_Competition } from "./steps/Step1_Competition";
import { Step2_Details } from "./steps/Step2_Details";
import { Step3_Invite } from "./steps/Step3_Invite";
import { useApiAuth } from "../hooks/useApiAuth";
import { usePoolOptions } from "../hooks/usePoolOptions";
import { usePools } from "../hooks/usePools";
import { apiUrl } from "../lib/api";
import { useLocalization } from "../lib/i18n";
import { resolvePoolWizardSubmitTarget } from "../lib/poolWizardSubmitMode";
import type { PoolCreateResponse, PoolMembership } from "../types/pools";
import { Button, StatusMessage } from "./ui";

type PoolWizardProps = {
  mode: 'create' | 'edit';
  pool?: PoolMembership | null;
  onClose: () => void;
};

export default function PoolWizard({ mode, pool, onClose }: PoolWizardProps) {
  const totalSteps = 2;
  const [step, setStep] = useState(mode === 'edit' ? 2 : 1);
  const { acquireToken } = useApiAuth();
  const { t } = useLocalization();
  const { poolOptions, isLoading: isLoadingOptions, error: poolOptionsError } = usePoolOptions();
  const { configureVirtualTraders, getVirtualTraderConfiguration } = usePools({ enabled: true });

  const [competitionCode, setCompetitionCode] = useState<string | null>(null);
  const [seasonCode, setSeasonCode] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [poolCode, setPoolCode] = useState<string | null>(
    mode === 'edit' && pool ? pool.pool_code : null
  );
  const [error, setError] = useState<string | null>(null);
  const [requiresAdminApproval, setRequiresAdminApproval] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [virtualTraderEnabled, setVirtualTraderEnabled] = useState(false);
  const [virtualTraderCount, setVirtualTraderCount] = useState(1);
  const [virtualTraderError, setVirtualTraderError] = useState<string | null>(null);
  const [virtualTraderSaving, setVirtualTraderSaving] = useState(false);
  const lastApprovalSettingsPoolRef = useRef<string | null>(null);
  const isManagingPool = mode === "edit" || Boolean(poolCode);

  useEffect(() => {
    if (mode === 'edit' && pool) {
      if (!poolCode) {
        setPoolCode(pool.pool_code);
      }
      setCompetitionCode(pool.competition_code);
      setSeasonCode(pool.season_code);
      setName(pool.pool_name);
      setDescription(pool.pool_description ?? '');
      if (lastApprovalSettingsPoolRef.current !== pool.pool_code) {
        setRequiresAdminApproval(pool.requires_admin_approval ?? true);
        lastApprovalSettingsPoolRef.current = pool.pool_code;
      }
    }
  }, [mode, pool, poolCode]);

  useEffect(() => {
    if (!poolCode || mode !== "edit") return;
    let cancelled = false;
    void getVirtualTraderConfiguration(poolCode).then((result) => {
      if (cancelled || !result.success || !result.data) return;
      setVirtualTraderEnabled(result.data.enabled);
      setVirtualTraderCount(Math.max(1, result.data.requested_count || 1));
    });
    return () => {
      cancelled = true;
    };
  }, [getVirtualTraderConfiguration, mode, poolCode]);

  const handleNext = async () => {
    if (step === 1) {
      // Create once, then update the created pool if the user returns to this step.
      const submitTarget = resolvePoolWizardSubmitTarget({ mode, poolCode });
      const url =
        submitTarget.action === 'create'
          ? apiUrl('/api/pools')
          : apiUrl(`/api/pools/${submitTarget.poolCode}`);
      const method = submitTarget.action === 'create' ? 'POST' : 'PUT';
      const body = {
        name,
        description,
        competition_code: competitionCode,
        season_code: seasonCode,
        requiresAdminApproval,
      };

      setIsSaving(true);
      try {
        setError(null);
        const accessToken = await acquireToken();
        if (!accessToken) {
          throw new Error('Authentication failed.');
        }

        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('API Error:', errorData);
          throw new Error(errorData.detail || `Failed to ${submitTarget.action} pool.`);
        }

        if (submitTarget.action === 'create') {
          const result: PoolCreateResponse = await response.json();
          console.log('API Response:', result);
          console.log('poolCode from response:', result.poolCode);
          // Try both camelCase and snake_case
          const code = result.poolCode || (result as { pool_code?: string }).pool_code;
          if (!code) {
            console.error('Full response:', JSON.stringify(result, null, 2));
            throw new Error('Pool code not found in response');
          }
          setPoolCode(code);
        }
        setStep(step + 1);
      } catch (err) {
        console.error('Error in handleNext:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      } finally {
        setIsSaving(false);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => setStep(step - 1);

  // Validation for each step
  const canProceedStep1 = Boolean(name.trim() && competitionCode && seasonCode);
  const canProceed = step === 1 ? canProceedStep1 : true;

  const handleInvite = async (emails: string[]) => {
    if (!poolCode) return;

    const accessToken = await acquireToken();
    if (!accessToken) return;

    const response = await fetch(apiUrl(`/api/pools/${poolCode}/invites`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(emails),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to send invites.");
    }
  };

  const handleVirtualTraderSave = async () => {
    if (!poolCode) return;
    setVirtualTraderSaving(true);
    setVirtualTraderError(null);
    const result = await configureVirtualTraders(
      poolCode,
      virtualTraderEnabled,
      virtualTraderEnabled ? virtualTraderCount : 0,
    );
    if (!result.success) setVirtualTraderError(result.error || "Could not update virtual traders.");
    setVirtualTraderSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 py-8"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-xl flex-col rounded border border-slate-700 bg-slate-950 text-foreground shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-800 px-4 py-2">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Step {step} of {totalSteps}</p>
            <h1 className="text-sm font-semibold uppercase tracking-wider text-slate-100">
              {isManagingPool ? "Manage Pool" : "Create Pool"}
            </h1>
            {!isManagingPool ? (
              <p className="mt-1 text-xs text-slate-500">
                Name your pool and pick a league season.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="px-2 text-lg leading-none text-slate-400 transition hover:text-slate-100"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </header>

        <div className="space-y-3 p-3 md:p-4">
          {isLoadingOptions && !poolOptions && <StatusMessage>Loading pool options...</StatusMessage>}
          {error && <StatusMessage variant="error">{error}</StatusMessage>}

          {step === 1 && (
            <div className="space-y-4">
              <Step2_Details
                name={name}
                description={description}
                onNameChange={setName}
                onDescriptionChange={setDescription}
              />
              <button
                type="button"
                role="switch"
                aria-checked={requiresAdminApproval}
                aria-disabled={isSaving}
                disabled={isSaving}
                onClick={() => setRequiresAdminApproval((value) => !value)}
                className={`flex w-full items-start justify-between gap-4 rounded border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-amber-400/60 disabled:cursor-not-allowed disabled:opacity-60 ${
                  requiresAdminApproval
                    ? "border-amber-400/60 bg-amber-400/10"
                    : "border-slate-800 bg-slate-900/60 hover:border-slate-700"
                }`}
              >
                <span>
                  <span className="block text-sm font-semibold uppercase tracking-wider text-slate-100">
                    {t("poolWizard.approval.title")}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-slate-400">
                    {t("poolWizard.approval.description")}
                  </span>
                </span>
                <span
                  className={`shrink-0 rounded border px-3 py-1 text-xs font-semibold ${
                    requiresAdminApproval
                      ? "border-amber-400 bg-amber-400 text-slate-950"
                      : "border-slate-700 bg-slate-800 text-slate-300"
                  }`}
                >
                  {requiresAdminApproval
                    ? t("poolWizard.approval.required")
                    : t("poolWizard.approval.notRequired")}
                </span>
              </button>
              <Step1_Competition
                competitionCode={competitionCode}
                seasonCode={seasonCode}
                onCompetitionChange={(nextCompetitionCode, nextSeasonCode) => {
                  setCompetitionCode(nextCompetitionCode);
                  setSeasonCode(nextSeasonCode);
                }}
                isEditMode={isManagingPool}
                poolOptions={poolOptions}
                isLoading={isLoadingOptions}
                error={poolOptionsError}
              />
            </div>
          )}

          {step === 2 &&
            (poolCode ? (
              <div className="space-y-4">
                {isManagingPool && (
                  <section className="rounded border border-slate-800 bg-slate-900/60 p-4" aria-label="Virtual traders">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-100">Virtual traders</h2>
                        <p className="mt-1 text-xs leading-5 text-slate-400">
                          Arm up to four clearly labeled, platform-funded liquidity participants. They start trading after playoff cutover, use cached odds, and never trade with one another.
                        </p>
                      </div>
                      <button
                        type="button"
                        className={`rounded px-3 py-1 text-xs font-semibold ${virtualTraderEnabled ? "bg-amber-400 text-slate-950" : "bg-slate-800 text-slate-300"}`}
                        onClick={() => setVirtualTraderEnabled((value) => !value)}
                        aria-pressed={virtualTraderEnabled}
                      >
                        {virtualTraderEnabled ? "Enabled" : "Disabled"}
                      </button>
                    </div>
                    {virtualTraderEnabled && (
                      <div className="mt-3 flex items-center gap-2" role="group" aria-label="Number of virtual traders">
                        {[1, 2, 3, 4].map((count) => (
                          <button
                            key={count}
                            type="button"
                            className={`h-9 w-9 rounded border text-sm ${virtualTraderCount === count ? "border-amber-400 bg-amber-400/15 text-amber-300" : "border-slate-700 text-slate-400"}`}
                            onClick={() => setVirtualTraderCount(count)}
                            aria-pressed={virtualTraderCount === count}
                          >
                            {count}
                          </button>
                        ))}
                        <span className="ml-2 text-xs text-slate-400">balanced profiles</span>
                      </div>
                    )}
                    {virtualTraderError && <StatusMessage variant="error">{virtualTraderError}</StatusMessage>}
                    <div className="mt-3 flex justify-end">
                      <Button type="button" variant="secondary" onClick={handleVirtualTraderSave} disabled={virtualTraderSaving}>
                        {virtualTraderSaving ? "Saving…" : "Save virtual traders"}
                      </Button>
                    </div>
                  </section>
                )}
                <Step3_Invite poolCode={poolCode} onInvite={handleInvite} />
              </div>
            ) : (
              <StatusMessage variant="error">
                Unable to load pool information. Please go back and try again.
              </StatusMessage>
            ))}
        </div>

        <footer className="flex justify-end gap-2 border-t border-slate-800 px-4 py-3">
          {step > 1 && (
            <Button type="button" variant="secondary" onClick={handleBack}>
              Back
            </Button>
          )}
          {step < totalSteps && (
            <Button type="button" onClick={handleNext} disabled={!canProceed || isSaving}>
              {isSaving ? "Saving…" : isManagingPool ? "Save" : "Create Pool"}
            </Button>
          )}
          {step === totalSteps && (
            <Button type="button" onClick={onClose}>
              Finish
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}
